/**
 * extensions/cornerstone/src/modules/suv/computeRoiSuv.js
 *
 * Per-ROI SUV statistics + SUV histogram from fused PET image data.
 *
 * E-1 changes:
 *   1. getPetSuvFactors is now called BEFORE the per-slice loop.
 *      Rationale: the histogram requires SUV-space values per voxel, not
 *      mo-space values. Having factors upfront lets samplePetSliceMo do
 *      the mo→SUV conversion inline and bin each voxel in one pass.
 *      Early-fail if factors are unavailable avoids loading images at all.
 *
 *   2. samplePetSliceMo now takes `factors` and builds histogram bins
 *      in the same voxel loop that computes max/min/sum/sumSq.
 *      Returns the per-slice histogram fragment (bins + overflow count)
 *      for accumulation by the caller.
 *
 *   3. computeRoiSuv now takes `petVoxelVolumeMm3` (computed once per
 *      timepoint in computeAllRoiSuvAcrossTimepoints). It is returned
 *      in the result so the bridge entry writer can stamp it.
 *
 * Histogram spec (§E.2):
 *   binWidth  = 0.05 SUV units
 *   binCount  = 400  → covers SUV 0.00–20.00
 *   overflow  = count of voxels with SUV ≥ 20 (PSMA PET outliers; rare)
 *   underflow = negative-SUV voxels (background air near contour boundary)
 *               are silently skipped — they cannot be PSMA-positive.
 *   Memory    = 400 × 4 bytes (Int32Array) ≈ 1.6 KB per ROI per timepoint.
 *
 * After PLAN-E histograms replace per-ROI PSMA(+)/(−) classification.
 * Query: countVoxelsAboveThreshold(histogram, thresholdSuv) runs in ~400
 * integer adds (negligible). See psmaPosVolumeUtils.js.
 */

import cornerstone from 'cornerstone-core';
import { moToSuv, getPetSuvFactors } from './suvComputation';

export const HISTOGRAM_BIN_WIDTH = 0.05;
export const HISTOGRAM_BIN_COUNT = 400;

export function buildCtToPetImageIdMap(ctImageIds, petImageIds) {
  const map = new Map();
  const n = Math.min(ctImageIds.length, petImageIds.length);
  for (let i = 0; i < n; i++) map.set(ctImageIds[i], petImageIds[i]);
  return map;
}

function ctPixelToPatient(ctPoints, ctImageId) {
  const meta = cornerstone.metaData.get('imagePlaneModule', ctImageId);
  if (!meta) return [];
  const {
    imagePositionPatient: ipp,
    rowCosines: r,
    columnCosines: c,
    rowPixelSpacing: rs,
    columnPixelSpacing: cs,
  } = meta;
  const out = new Array(ctPoints.length);
  for (let i = 0; i < ctPoints.length; i++) {
    const col = ctPoints[i].x;
    const row = ctPoints[i].y;
    out[i] = [
      ipp[0] + col * cs * r[0] + row * rs * c[0],
      ipp[1] + col * cs * r[1] + row * rs * c[1],
      ipp[2] + col * cs * r[2] + row * rs * c[2],
    ];
  }
  return out;
}

function patientToImagePixel(patientPoints, imageMeta) {
  const {
    imagePositionPatient: ipp,
    rowCosines: r,
    columnCosines: c,
    rowPixelSpacing: rs,
    columnPixelSpacing: cs,
  } = imageMeta;
  const xs = new Array(patientPoints.length);
  const ys = new Array(patientPoints.length);
  for (let i = 0; i < patientPoints.length; i++) {
    const [px, py, pz] = patientPoints[i];
    const dx = px - ipp[0],
      dy = py - ipp[1],
      dz = pz - ipp[2];
    xs[i] = (dx * r[0] + dy * r[1] + dz * r[2]) / cs;
    ys[i] = (dx * c[0] + dy * c[1] + dz * c[2]) / rs;
  }
  return { xs, ys };
}

function pointInPolygon(px, py, polyXs, polyYs) {
  let inside = false;
  const n = polyXs.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polyXs[i],
      yi = polyYs[i];
    const xj = polyXs[j],
      yj = polyYs[j];
    const intersect =
      yi > py !== yj > py &&
      px < ((xj - xi) * (py - yi)) / (yj - yi || 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Sample one PET slice, building histogram bins in the same pass.
 *
 * @param {string} petImageId
 * @param {Array}  patientPoints  3D LPS coords of the polygon vertices
 * @param {object} factors        PET SUV factors (decayCorrectedDoseBq, patientWeightKg)
 * @returns {{
 *   maxMo, minMo, sumMo, sumSqMo, count,
 *   histBins: Int32Array,   // partial histogram for this slice
 *   histOverflow: number    // voxels with SUV ≥ 20 in this slice
 * }}
 */
async function samplePetSliceMo(petImageId, patientPoints, factors) {
  const image = await cornerstone.loadAndCacheImage(petImageId);
  const meta = cornerstone.metaData.get('imagePlaneModule', petImageId);
  if (!meta) {
    return {
      maxMo: -Infinity,
      minMo: Infinity,
      sumMo: 0,
      sumSqMo: 0,
      count: 0,
      histBins: new Int32Array(HISTOGRAM_BIN_COUNT),
      histOverflow: 0,
    };
  }

  const { xs, ys } = patientToImagePixel(patientPoints, meta);
  const W = image.width,
    H = image.height;

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (let i = 0; i < xs.length; i++) {
    if (xs[i] < minX) minX = xs[i];
    if (ys[i] < minY) minY = ys[i];
    if (xs[i] > maxX) maxX = xs[i];
    if (ys[i] > maxY) maxY = ys[i];
  }
  minX = Math.max(0, Math.floor(minX));
  minY = Math.max(0, Math.floor(minY));
  maxX = Math.min(W - 1, Math.ceil(maxX));
  maxY = Math.min(H - 1, Math.ceil(maxY));

  const empty = {
    maxMo: -Infinity,
    minMo: Infinity,
    sumMo: 0,
    sumSqMo: 0,
    count: 0,
    histBins: new Int32Array(HISTOGRAM_BIN_COUNT),
    histOverflow: 0,
  };
  if (maxX < minX || maxY < minY) return empty;

  const pd = image.getPixelData();
  const slope = image.slope || 1;
  const intercept = image.intercept || 0;
  const suvScale =
    (factors.patientWeightKg * 1000) / factors.decayCorrectedDoseBq;

  let maxMo = -Infinity,
    minMo = Infinity,
    sumMo = 0,
    sumSqMo = 0,
    count = 0;
  const histBins = new Int32Array(HISTOGRAM_BIN_COUNT);
  let histOverflow = 0;

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      if (pointInPolygon(x + 0.5, y + 0.5, xs, ys)) {
        const mo = pd[y * W + x] * slope + intercept;
        if (mo > maxMo) maxMo = mo;
        if (mo < minMo) minMo = mo;
        sumMo += mo;
        sumSqMo += mo * mo;
        count++;

        // Histogram binning in SUV space (same conversion as moToSuv, no call overhead)
        const suv = mo * suvScale;
        if (suv < 0) continue; // skip negative-SUV background voxels
        const binIdx = Math.floor(suv / HISTOGRAM_BIN_WIDTH);
        if (binIdx >= HISTOGRAM_BIN_COUNT) {
          histOverflow++;
        } else {
          histBins[binIdx]++;
        }
      }
    }
  }
  return { maxMo, minMo, sumMo, sumSqMo, count, histBins, histOverflow };
}

/**
 * Compute SUV statistics + histogram for one ROI across its per-CT-slice polygons.
 *
 * @param {object} args
 * @param {Array<{ctImageId, points}>} args.contoursByCtSlice
 * @param {Map<string,string>}         args.ctToPetImageIdMap
 * @param {number|null}                args.petVoxelVolumeMm3  passed from orchestrator
 * @returns {Promise<{
 *   suvMax, suvMean, suvMin, suvSD,
 *   voxelCount, factors,
 *   suvHistogram: { binWidth, binCount, bins: Int32Array, overflow },
 *   petVoxelVolumeMm3
 * }>}
 */
export async function computeRoiSuv({
  contoursByCtSlice,
  ctToPetImageIdMap,
  petVoxelVolumeMm3,
}) {
  const nullResult = {
    suvMax: null,
    suvMean: null,
    suvMin: null,
    suvSD: null,
    voxelCount: 0,
    factors: { ok: false, reason: 'no-contours' },
    suvHistogram: null,
    petVoxelVolumeMm3: petVoxelVolumeMm3 ?? null,
  };

  if (!contoursByCtSlice?.length) return nullResult;

  // ── Pre-flight: resolve SUV factors from the first available PET imageId ──
  // Must happen before any slice loop so samplePetSliceMo has factors for
  // SUV-space histogram binning.
  let factors = null;
  for (const { ctImageId } of contoursByCtSlice) {
    const petId = ctToPetImageIdMap.get(ctImageId);
    if (petId) {
      const f = getPetSuvFactors(petId);
      if (f?.ok) {
        factors = f;
        break;
      }
    }
  }
  if (!factors?.ok) {
    return {
      ...nullResult,
      factors: factors || { ok: false, reason: 'no-pet-factors' },
    };
  }

  let maxMo = -Infinity,
    minMo = Infinity,
    sumMo = 0,
    sumSqMo = 0,
    totalCount = 0;
  // Accumulated histogram across all slices
  const accumBins = new Int32Array(HISTOGRAM_BIN_COUNT);
  let accumOverflow = 0;

  for (const { ctImageId, points } of contoursByCtSlice) {
    const petImageId = ctToPetImageIdMap.get(ctImageId);
    if (!petImageId || !points || points.length < 3) continue;

    const patientPoints = ctPixelToPatient(points, ctImageId);
    if (patientPoints.length < 3) continue;

    const {
      maxMo: sliceMax,
      minMo: sliceMin,
      sumMo: s,
      sumSqMo: sq,
      count,
      histBins,
      histOverflow,
    } = await samplePetSliceMo(petImageId, patientPoints, factors);

    if (count === 0) continue;
    if (sliceMax > maxMo) maxMo = sliceMax;
    if (sliceMin < minMo) minMo = sliceMin;
    sumMo += s;
    sumSqMo += sq;
    totalCount += count;
    for (let i = 0; i < HISTOGRAM_BIN_COUNT; i++) accumBins[i] += histBins[i];
    accumOverflow += histOverflow;
  }

  if (totalCount === 0) {
    return { ...nullResult, factors, suvHistogram: null };
  }

  const { decayCorrectedDoseBq, patientWeightKg } = factors;
  const meanMo = sumMo / totalCount;
  const varMo = Math.max(0, sumSqMo / totalCount - meanMo * meanMo);
  const sdMo = Math.sqrt(varMo);

  // moToSuv is linear (no offset) → SD(SUV) = moToSuv(SD(mo))
  const suvMax = moToSuv(maxMo, decayCorrectedDoseBq, patientWeightKg);
  const suvMean = moToSuv(meanMo, decayCorrectedDoseBq, patientWeightKg);
  const suvMin = moToSuv(minMo, decayCorrectedDoseBq, patientWeightKg);
  const suvSD = moToSuv(sdMo, decayCorrectedDoseBq, patientWeightKg);

  // Sanity: histogram voxel sum should equal totalCount (all voxels were in SUV ≥ 0 or skipped)
  // Not asserted at runtime but useful for console verification (E-1 §verify).

  return {
    suvMax,
    suvMean,
    suvMin,
    suvSD,
    voxelCount: totalCount,
    factors,
    suvHistogram: {
      binWidth: HISTOGRAM_BIN_WIDTH,
      binCount: HISTOGRAM_BIN_COUNT,
      bins: accumBins,
      overflow: accumOverflow,
    },
    petVoxelVolumeMm3: petVoxelVolumeMm3 ?? null,
  };
}
